import React from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  useTheme,
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { RootState } from '../app/store';

const generateProgressData = (userEnrollments: any[]) => {
  // Deduplicate enrollments first
  const uniqueEnrollmentsMap = new Map<string, any>();
  
  (userEnrollments || []).forEach(enrollment => {
    if (!enrollment.assignment) return;
    
    let contentId = '';
    if (enrollment.assignment.content_type === 'course') {
      const courseId = typeof enrollment.assignment.course === 'object' 
        ? enrollment.assignment.course?.id 
        : enrollment.assignment.course;
      contentId = `course_${courseId}`;
    } else if (enrollment.assignment.content_type === 'assessment') {
      const assessmentId = typeof enrollment.assignment.assessment === 'object'
        ? enrollment.assignment.assessment?.id
        : enrollment.assignment.assessment;
      contentId = `assessment_${assessmentId}`;
    }
    
    if (!contentId) return;
    
    const existing = uniqueEnrollmentsMap.get(contentId);
    
    // Logic to pick the "best" enrollment (highest progress or completed status)
    if (!existing) {
      uniqueEnrollmentsMap.set(contentId, enrollment);
    } else {
      const currentProgress = Number(enrollment.progress) || 0;
      const existingProgress = Number(existing.progress) || 0;
      
      // Prefer completed
      if (enrollment.status === 'completed' && existing.status !== 'completed') {
        uniqueEnrollmentsMap.set(contentId, enrollment);
      }
      // Prefer higher progress
      else if (currentProgress > existingProgress) {
        uniqueEnrollmentsMap.set(contentId, enrollment);
      }
      // Prefer in_progress over assigned
      else if (enrollment.status === 'in_progress' && existing.status === 'assigned') {
        uniqueEnrollmentsMap.set(contentId, enrollment);
      }
    }
  });
  
  const uniqueEnrollments = Array.from(uniqueEnrollmentsMap.values());

  // Filter to only include courses and standalone assessments
  // Exclude course-embedded assessments to avoid double-counting as they are part of the course
  const filteredEnrollments = uniqueEnrollments.filter(ue => {
    if (ue.content_type === 'course') {
      return true; // Include all courses
    }
    if (ue.content_type === 'assessment') {
      return ue.is_standalone_assessment === true; // Only standalone assessments
    }
    return false;
  });

  const total = filteredEnrollments.length;
  if (total === 0) {
    return [
      { name: 'Completed', value: 0, color: '#37B256' },
      { name: 'In Progress', value: 0, color: '#E96D17' },
      { name: 'Not Started', value: 0, color: '#B9B9B9' },
    ];
  }

  let completed = 0;
  let inProgress = 0;
  let notStarted = 0;

  filteredEnrollments.forEach(ue => {
    // Universal logic for all content types (courses and standalone assessments)
    // status can be: 'assigned', 'in_progress', 'completed', 'overdue'
    
    if (ue.status === 'completed') {
      completed++;
    } else if (ue.status === 'in_progress' || ue.status === 'overdue') {
      // Both in_progress and overdue count as "In Progress"
      inProgress++;
    } else if (ue.status === 'assigned') {
      // 'assigned' means not yet started
      notStarted++;
    } else {
      // Fallback for any unknown status
      notStarted++;
    }
  });

  return [
    { name: 'Completed', value: Math.round((completed / total) * 100), color: '#37B256' },
    { name: 'In Progress', value: Math.round((inProgress / total) * 100), color: '#E96D17' },
    { name: 'Not Started', value: Math.round((notStarted / total) * 100), color: '#B9B9B9' },
  ];
};

export const ProgressChart: React.FC<{ size?: number }> = ({
  size = 150,
}) => {
  const { user } = useSelector((state: RootState) => state.auth);
  // Backend now returns only current user's enrollments via only_mine=true parameter
  const { userEnrollments } = useSelector((state: RootState) => state.assignments);
  
  const data = generateProgressData(userEnrollments || []);

  return (
    <Card>
      <CardContent sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" fontWeight="bold" mb={2}>
          Progress Overview
        </Typography>
        
        <Box sx={{ position: 'relative', display: 'inline-block' }}>
          <PieChart width={size} height={size}>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={size * 0.25}
              outerRadius={size * 0.4}
              paddingAngle={5}
              dataKey="value"
            >
              {data.map((entry: any, index: number) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
          </PieChart>
          
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
            }}
          >
            <Typography variant="h4" fontWeight="bold" color="primary.main">
              {data[0]?.value || 0}%
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Complete
            </Typography>
          </Box>
        </Box>

        <Box display="flex" justifyContent="center" gap={1} mt={2} flexWrap="wrap">
          {data.map((item: any, index: number) => (
            <Chip
              key={index}
              label={`${item.name}: ${item.value}%`}
              size="small"
              sx={{
                bgcolor: item.color + '20',
                color: item.color,
                fontWeight: 500,
                fontSize: '0.75rem',
              }}
            />
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

export default ProgressChart;
